/**
 * AI Trial Room — Shop Owner: My Branding + My Analytics widget
 * Each user manages their own shop_name + logo_url + views their private stats.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Save, Image as ImageIcon, TrendingUp, TrendingDown, Package, Loader2 } from 'lucide-react';
import api from '../../utils/api';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

export default function TrialRoomOwnerPanel() {
  const [branding, setBranding] = useState({ enabled: true, shop_name: '', shop_logo_url: '', is_custom: false });
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [showBrandingForm, setShowBrandingForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, s] = await Promise.all([
        api.get('/trial-room/my-branding').catch(() => ({ data: null })),
        api.get('/trial-room/my-stats').catch(() => ({ data: null })),
      ]);
      if (b.data) setBranding(b.data);
      if (s.data) setStats(s.data);
    } catch (_) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveBranding = async () => {
    setBusy(true);
    try {
      const r = await api.post('/trial-room/my-branding', {
        shop_name: branding.shop_name,
        shop_logo_url: branding.shop_logo_url,
        watermark_enabled: branding.enabled,
      });
      setBranding(r.data);
      toast.success('✅ Aapki shop branding save ho gayi — ab har download/share pe aayegi');
      setShowBrandingForm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const trend = stats?.trend_pct_wow || 0;
  const trendUp = trend >= 0;

  return (
    <div className="rounded-2xl border-2 border-cyan-400/40 bg-gradient-to-br from-slate-950/80 via-cyan-950/30 to-slate-950/80 p-4 space-y-3" data-testid="trial-owner-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">🏪 My Shop Dashboard</div>
          <h3 className="text-lg font-black text-white">Branding + Analytics (private to you)</h3>
        </div>
        {branding.is_custom ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 font-black">✓ CUSTOM BRANDING ACTIVE</span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-400/40 font-black">USING PLATFORM DEFAULT</span>
        )}
      </div>

      {/* Branding section */}
      <div className="rounded-xl border border-white/10 bg-black/30 p-3">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <div className="flex items-center gap-2 min-w-0">
            {branding.shop_logo_url ? (
              <img src={branding.shop_logo_url} alt="logo" className="w-10 h-10 rounded object-contain bg-black/40 border border-white/10 flex-shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <div className="w-10 h-10 rounded bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0"><ImageIcon className="w-4 h-4 text-slate-500" /></div>
            )}
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-widest text-slate-400">Shop Name (on watermark)</div>
              <div className="text-sm font-black text-white truncate">{branding.shop_name || '(not set)'}</div>
            </div>
          </div>
          <button
            onClick={() => setShowBrandingForm((v) => !v)}
            data-testid="owner-edit-branding-btn"
            className="text-[11px] px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white font-black">
            {showBrandingForm ? 'Cancel' : '✏️ Edit Branding'}
          </button>
        </div>
        {showBrandingForm && (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <input
              placeholder="Shop Name (e.g. Anil's Boutique)"
              value={branding.shop_name}
              onChange={(e) => setBranding({ ...branding, shop_name: e.target.value })}
              data-testid="owner-shop-name-input"
              className="bg-white/5 border border-white/10 rounded px-2 py-1.5"
              maxLength={80}
            />
            <input
              placeholder="Logo URL (png/jpg — transparent bg best)"
              value={branding.shop_logo_url}
              onChange={(e) => setBranding({ ...branding, shop_logo_url: e.target.value })}
              data-testid="owner-logo-url-input"
              className="bg-white/5 border border-white/10 rounded px-2 py-1.5"
            />
            <label className="flex items-center gap-1.5 text-[11px] text-slate-300 col-span-full">
              <input
                type="checkbox"
                checked={branding.enabled}
                onChange={(e) => setBranding({ ...branding, enabled: e.target.checked })}
                data-testid="owner-wm-enabled"
                className="accent-cyan-400"
              />
              Enable watermark on my downloads / shares
            </label>
            <button
              onClick={saveBranding}
              disabled={busy}
              data-testid="owner-branding-save-btn"
              className="col-span-full px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white text-xs font-black flex items-center justify-center gap-1 disabled:opacity-50 hover:brightness-110"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3" /> Save My Branding</>}
            </button>
          </div>
        )}
      </div>

      {/* Stats section */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* KPIs */}
          <div className="md:col-span-1 space-y-2">
            <div className="rounded-xl border border-white/10 bg-black/30 p-3">
              <div className="text-[9px] uppercase tracking-widest text-slate-400">Today&apos;s Trials</div>
              <div className="text-3xl font-black text-emerald-300 font-mono">{fmt(stats.today)}</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/30 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-slate-400">Week (7d)</div>
                  <div className="text-2xl font-black text-cyan-300 font-mono">{fmt(stats.week)}</div>
                </div>
                <div className={`text-xs font-black flex items-center gap-0.5 ${trendUp ? 'text-emerald-300' : 'text-red-300'}`}>
                  {trendUp ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {trend >= 0 ? '+' : ''}{trend}%
                </div>
              </div>
              <div className="text-[9px] text-slate-500 mt-0.5">vs previous week ({fmt(stats.prev_week)})</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/30 p-3">
              <div className="text-[9px] uppercase tracking-widest text-slate-400">All-Time Trials</div>
              <div className="text-2xl font-black text-amber-300 font-mono">{fmt(stats.total)}</div>
            </div>
          </div>

          {/* ROI Table */}
          <div className="md:col-span-2 rounded-xl border-2 border-amber-400/30 bg-amber-500/5 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Package className="w-4 h-4 text-amber-300" />
              <div className="text-sm font-black text-amber-300">🔥 Product ROI (30 days)</div>
            </div>
            {(!stats.top_products || stats.top_products.length === 0) ? (
              <div className="text-[11px] text-slate-500 italic py-4 text-center">
                Koi tracked product nahi. Shop Owner 2-Camera flow me &quot;Choose from Catalog&quot; se try-on karo — data yahan aayega.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-400 uppercase text-[9px]">
                    <tr>
                      <th className="text-left py-1">#</th>
                      <th className="text-left py-1">Product</th>
                      <th className="text-right py-1">Try-Ons</th>
                      <th className="text-right py-1">Est. Conv.</th>
                      <th className="text-right py-1">Est. Revenue*</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.top_products.map((p, i) => (
                      <tr key={p.product_id || i} className="border-t border-white/5" data-testid={`owner-roi-row-${i}`}>
                        <td className="py-1 text-amber-300 font-black">#{i + 1}</td>
                        <td className="py-1 text-white truncate max-w-[160px]">{p.product_name || '(unknown)'}</td>
                        <td className="py-1 text-right font-mono text-cyan-300">{p.trials}</td>
                        <td className="py-1 text-right font-mono text-emerald-300">{p.est_conversion_pct}%</td>
                        <td className="py-1 text-right font-mono text-amber-300">₹{fmt(p.est_revenue_inr)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="text-[9px] text-slate-500 mt-1 italic">* Heuristic estimate: try-on density × 15-30% conversion × ₹500 avg ticket. Actual conversions depend on POS integration.</div>
              </div>
            )}

            {/* Character split mini */}
            {stats.character_stats && stats.character_stats.length > 0 && (
              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                <span className="text-[9px] uppercase tracking-widest text-slate-400 font-black">Customer Mix:</span>
                {stats.character_stats.map((c) => (
                  <span key={c.character} className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 text-fuchsia-200 font-bold uppercase">
                    {c.character} · {c.count}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
